import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  cpGetDomainManagement,
  cpSetAutoRenew,
  type DomainManagementInfo,
} from '@/lib/api/controlPlaneClient'
import { toast } from 'sonner'

const MANAGEMENT_QUERY_KEY = ['domain-management'] as const

export function useDomainManagement(enabled: boolean) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: MANAGEMENT_QUERY_KEY,
    queryFn: cpGetDomainManagement,
    enabled,
  })

  const autoRenewMutation = useMutation({
    mutationFn: (newValue: boolean) => cpSetAutoRenew(newValue),
    onMutate: async (newValue) => {
      await queryClient.cancelQueries({ queryKey: MANAGEMENT_QUERY_KEY })
      const previous = queryClient.getQueryData<DomainManagementInfo>(MANAGEMENT_QUERY_KEY)
      queryClient.setQueryData<DomainManagementInfo | undefined>(
        MANAGEMENT_QUERY_KEY,
        (old) => old ? { ...old, auto_renew: newValue } : old
      )
      return { previous }
    },
    onError: (_err, _newValue, context) => {
      if (context?.previous) {
        queryClient.setQueryData(MANAGEMENT_QUERY_KEY, context.previous)
      }
      toast.error('Failed to update auto-renewal setting')
    },
    onSuccess: () => {
      toast.success('Auto-renewal updated')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY })
    },
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    toggleAutoRenew: autoRenewMutation.mutate,
    isToggling: autoRenewMutation.isPending,
  }
}
